import {
	FIXED_API_VARIABLES,
	type ApiVariableDefinition,
	type IntegrationSummary,
	type MembershipSummary,
} from '@livechat/shared';
import { type FormEvent, useEffect, useState } from 'react';

import { getIntegration, updateIntegration } from '../api.js';

function normaliseName(value: string): string {
	return value
		.toLowerCase()
		.replaceAll(/[^a-z0-9_]/g, '_')
		.replaceAll(/_+/g, '_');
}

function LockIcon() {
	return (
		<svg aria-hidden="true" fill="none" viewBox="0 0 24 24">
			<path d="M7.5 10V7.5a4.5 4.5 0 0 1 9 0V10" />
			<path d="M6 10h12v10H6z" />
		</svg>
	);
}

function TrashIcon() {
	return (
		<svg aria-hidden="true" fill="none" viewBox="0 0 24 24">
			<path d="M4 7h16M9 7V4h6v3m3 0-1 13H7L6 7m4 4v5m4-5v5" />
		</svg>
	);
}

export function TenantApi({ membership }: { membership: MembershipSummary }) {
	const [integration, setIntegration] = useState<IntegrationSummary>();
	const [variables, setVariables] = useState<ApiVariableDefinition[]>([]);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string>();
	const [message, setMessage] = useState<string>();

	useEffect(() => {
		let current = true;
		setError(undefined);
		void getIntegration(membership.tenantId)
			.then((loaded) => {
				if (!current) return;
				setIntegration(loaded);
				setVariables(loaded.operationalSettings.apiVariables);
			})
			.catch((caught: unknown) => {
				if (current)
					setError(
						caught instanceof Error ? caught.message : 'Could not load API settings',
					);
			});
		return () => {
			current = false;
		};
	}, [membership.tenantId]);

	function changeVariable(index: number, change: Partial<ApiVariableDefinition>): void {
		setVariables((current) =>
			current.map((variable, currentIndex) =>
				currentIndex === index ? { ...variable, ...change } : variable,
			),
		);
		setMessage(undefined);
	}

	async function save(event: FormEvent<HTMLFormElement>): Promise<void> {
		event.preventDefault();
		if (!integration || busy) return;
		setBusy(true);
		setError(undefined);
		setMessage(undefined);
		try {
			const updated = await updateIntegration(membership.tenantId, {
				operationalSettings: {
					...integration.operationalSettings,
					apiVariables: variables.map(({ name, title }) => ({
						name: name.trim().toLowerCase(),
						title: title.trim(),
					})),
				},
			});
			setIntegration(updated);
			setVariables(updated.operationalSettings.apiVariables);
			setMessage('API variables saved.');
		} catch (caught) {
			setError(caught instanceof Error ? caught.message : 'Could not save API settings');
		} finally {
			setBusy(false);
		}
	}

	return (
		<div className="settings-page integration-settings-page">
			{error && <div className="alert error">{error}</div>}
			{message && <div className="alert success">{message}</div>}
			<div className="settings-section-heading">
				<h4>API</h4>
				<p>Control the visitor data that websites may send to Livechat.</p>
			</div>
			{integration ? (
				<form className="integration-settings-form" onSubmit={(event) => void save(event)}>
					<section className="settings-card integration-panel api-variables-panel">
						<div className="integration-panel-heading">
							<h4 className="panel-title">Variables</h4>
							<p>
								Built-in variables are always available. Add only the custom data
								that your website is allowed to send.
							</p>
						</div>
						<div className="api-variable-table">
							<div className="api-variable-row api-variable-head" aria-hidden="true">
								<span>Variable name</span>
								<span>Variable title</span>
								<span />
							</div>
							{FIXED_API_VARIABLES.map((variable) => (
								<div className="api-variable-row fixed" key={variable.name}>
									<label>
										<span className="visually-hidden">Variable name</span>
										<input disabled value={variable.name} />
									</label>
									<label>
										<span className="visually-hidden">Variable title</span>
										<input disabled value={variable.title} />
									</label>
									<span
										aria-label="Built-in variable, locked"
										className="api-variable-lock"
										role="img"
										title="Built-in variable"
									>
										<LockIcon />
									</span>
								</div>
							))}
							{variables.map((variable, index) => (
								<div className="api-variable-row" key={index}>
									<label>
										<span className="visually-hidden">Variable name</span>
										<input
											aria-label={`Custom variable ${index + 1} name`}
											maxLength={40}
											onChange={(event) =>
												changeVariable(index, {
													name: normaliseName(event.currentTarget.value),
												})
											}
											pattern="[a-z][a-z0-9_]{0,39}"
											placeholder="order_number"
											required
											value={variable.name}
										/>
									</label>
									<label>
										<span className="visually-hidden">Variable title</span>
										<input
											aria-label={`Custom variable ${index + 1} title`}
											maxLength={80}
											onChange={(event) =>
												changeVariable(index, {
													title: event.currentTarget.value,
												})
											}
											placeholder="Order number"
											required
											value={variable.title}
										/>
									</label>
									<button
										aria-label={`Remove ${variable.title || variable.name || `custom variable ${index + 1}`}`}
										className="icon-button danger api-variable-remove"
										onClick={() =>
											setVariables((current) =>
												current.filter(
													(_, currentIndex) => currentIndex !== index,
												),
											)
										}
										type="button"
									>
										<TrashIcon />
									</button>
								</div>
							))}
						</div>
						<button
							className="secondary-button api-variable-add"
							disabled={variables.length >= 50}
							onClick={() =>
								setVariables((current) => [...current, { name: '', title: '' }])
							}
							type="button"
						>
							Add variable
						</button>
						<p className="api-variable-example">
							Use the variable name with <code>ManorsoftLivechat.set()</code>. Values
							may be plain text or a safe link containing a value and an HTTP or HTTPS
							URL.
						</p>
					</section>
					<button className="primary-button integration-save" disabled={busy}>
						{busy ? 'Saving…' : 'Save API settings'}
					</button>
				</form>
			) : (
				!error && <p>Loading API settings…</p>
			)}
		</div>
	);
}
